import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import './ActivityView.scss';
import { buyingMatchSubtitle } from './buyingMatchSubtitle';
import houseIcon from '../common/icons/house.png';
import coinIcon from '../common/icons/coin.png';
import type { ActivityResponse, ActivitySection } from './activityApi';
import ActivityRowDetail from '../ActivityRowDetail/ActivityRowDetail';
import type { ActivityRowDetailRow } from '../ActivityRowDetail/ActivityRowDetail';
import ActivityRowVisual, { BuildingGlyph } from './ActivityRowVisual';
import type { RowVariant } from './ActivityRowVisual';
import type { AreaInterest } from '../AppContent/NewLandingPage/CouldBuyFlow/areaInterestApi';
import {
  areasLabel,
  criteriaStatsLabel,
} from '../AppContent/NewLandingPage/CouldBuyFlow/areaInterestCard';

type ActivityViewProps = {
  open: boolean;
  onClose: () => void;
  /** Mobile tab-slide direction (JAA-266): right on forward, left on back. */
  slideDir?: 'forward' | 'back';
  /** Live Activity payload from AppContent (JAA-284). */
  data?: ActivityResponse | null;
  isLoading?: boolean;
  /** Marks the tapped row seen. Opening its matches is handled locally. */
  onRowClick?: (section: ActivitySection, entityId: string) => void;
  onCouldSell?: () => void;
  onCouldBuy?: () => void;
  /** Switches the app to the Explore map (the "Explore the map" promo CTA). */
  onNavigateExplore?: () => void;
  isLoggedIn?: boolean;
  setLoginModalVisible?: (visible: boolean) => void;
  /** Deep-linked row (digest email CTA): its detail auto-opens once found in
   *  fresh data, as if tapped. Consumed via onInitialRowConsumed. */
  initialRow?: { section: ActivitySection; entityId: string } | null;
  onInitialRowConsumed?: () => void;
  /** A top layer (the single-listing view, or Messages) is covering the
   *  panel. It stays mounted so the open row and scroll survive,
   *  but is taken out of the paint entirely: its desktop panel outranks the
   *  modal stack (JAA-345, JAA-443). */
  covered?: boolean;
};

type ActivityRowData = {
  id: string;
  variant: RowVariant;
  title: string;
  subtitle: string;
  count?: number;
  image?: string | null;
  /** Identifies the row to /activity/matches when its detail view opens. */
  section: ActivitySection;
  entityId: string;
  onClick: () => void;
};

type ActivitySectionView = {
  id: string;
  title: string;
  rows: ActivityRowData[];
};

const DESKTOP_CARD_QUERY = '(min-width: 1025px)';

// Detail slide duration, matching the nav-page-appear transition the mobile
// panel already uses. Kept in sync with $detail-slide-ms in the detail's SCSS.
const DETAIL_SLIDE_MS = 180;

// Rows shown per section while collapsed (most active first, then latest);
// the header chevron expands to the full list. One each for now, so the three
// sections stay visible together without scrolling. Keyed by section id.
const SECTION_PREVIEW: Record<string, number> = {
  'my-places': 1,
  'buying-interests': 1,
  following: 1,
};

// Encouraging empty-state promo per section (text + CTA), shown whenever
// the section has no rows (an account with nothing at all included), nudging the
// user to add their first. Reuses the FAB action tones (sell = yellow, buy =
// teal). Action is resolved to the matching handler at render (sell →
// onCouldSell, buy → onCouldBuy, explore → onNavigateExplore).
type PromoAction = 'sell' | 'buy' | 'explore';
const SECTION_PROMOS: Record<
  string,
  {
    iconVariant: 'seller' | 'buyer' | 'building';
    textKey: string;
    ctaKey: string;
    tone: 'sell' | 'buy';
    action: PromoAction;
    // "Learn more" deep-links to the matching info.jaakko.com page, per language.
    learnUrl: { en: string; fi: string };
  }
> = {
  'my-places': {
    iconVariant: 'seller',
    textKey: 'newLandingPage.activity.promo.myPlaces.text',
    ctaKey: 'newLandingPage.activity.fab.couldSell',
    tone: 'sell',
    action: 'sell',
    learnUrl: {
      en: 'https://info.jaakko.com/en-gb/how-to-sell-your-apartment-yourself',
      fi: 'https://info.jaakko.com/nain-myyt-asunnon-itse',
    },
  },
  'buying-interests': {
    iconVariant: 'buyer',
    textKey: 'newLandingPage.activity.promo.buyingInterests.text',
    ctaKey: 'newLandingPage.activity.fab.interestInBuying',
    tone: 'buy',
    action: 'buy',
    learnUrl: {
      en: 'https://info.jaakko.com/en-gb#toimi-nain',
      fi: 'https://info.jaakko.com/#toimi-nain',
    },
  },
  following: {
    iconVariant: 'building',
    textKey: 'newLandingPage.activity.promo.following.text',
    ctaKey: 'newLandingPage.activity.promo.following.cta',
    tone: 'buy',
    action: 'explore',
    learnUrl: {
      en: 'https://info.jaakko.com/en-gb#ukk',
      fi: 'https://info.jaakko.com/#ukk',
    },
  },
};

/**
 * Whether a loaded section has no rows. Promo visibility also requires the
 * section list to be rendered; loading and detail views have no promo CTAs.
 */
const isSectionEmpty = (section: ActivitySectionView): boolean =>
  section.rows.length === 0;

// Stands in for a missing payload (a failed or not-yet-loaded /activity) so the
// sections render their empty states instead of a blank panel.
const EMPTY_ACTIVITY: ActivityResponse = {
  my_places: [],
  buying_interests: [],
  following: [],
  total_new: 0,
};

/**
 * Join a row's activity counts into one subtitle, dropping any that are zero so
 * a quiet row reads as blank rather than "0 likes, 0 new listing". Returns ''
 * when nothing is new, which the row renders as no subtitle at all.
 */
const activitySubtitle = (
  parts: [number, string][],
  t: TFunction,
): string =>
  parts
    .filter(([count]) => count > 0)
    .map(([count, key]) => t(key, { count }))
    .join(', ');

/**
 * "Street 19" from an anchor address, for labelling an area interest's circle
 * as "Street 19 ± 500 m". Empty when unknown, which leaves areasLabel on its
 * bare "500 m radius" fallback.
 */
const anchorStreet = (
  address: { street?: string; houseNumber?: string } | null | undefined,
): string | undefined =>
  address
    ? [address.street, address.houseNumber].filter(Boolean).join(' ') || undefined
    : undefined;

/** Format an address object of unknown shape into "street houseNumber, city", guarding missing fields. */
const formatAddress = (address: unknown): string => {
  if (!address || typeof address !== 'object') return '';
  const addr = address as Record<string, unknown>;
  const street = typeof addr.street === 'string' ? addr.street : '';
  const houseNumber =
    typeof addr.houseNumber === 'string' ? addr.houseNumber : '';
  const city = typeof addr.city === 'string' ? addr.city : '';
  const streetPart = [street, houseNumber].filter(Boolean).join(' ');
  return [streetPart, city].filter(Boolean).join(', ');
};

const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia(query);
    const update = () => setMatches(mediaQuery.matches);

    update();
    mediaQuery.addEventListener?.('change', update);

    return () => {
      mediaQuery.removeEventListener?.('change', update);
    };
  }, [query]);

  return matches;
};

const PlusIcon = () => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 30 30"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M15 6.25V23.75M6.25 15H23.75"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
);

const CloseGlyph = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M4.5 4.5L15.5 15.5M15.5 4.5L4.5 15.5"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
    />
  </svg>
);

const ChevronIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M5 7.5L10 12.5L15 7.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Single shimmer placeholder row shown while activity data is loading. */
const ShimmerRow = () => (
  <div className="activity-view__row" aria-hidden="true">
    <div className="activity-view__row-visual activity-view__row-visual--shimmer" />
    <div className="activity-view__row-body">
      <span
        className="activity-view__row-divider activity-view__row-divider--top"
        aria-hidden="true"
      />
      <div className="activity-view__row-main">
        <div className="activity-view__row-text">
          <div className="activity-view__row-shimmer-title" />
          <div className="activity-view__row-shimmer-subtitle" />
        </div>
      </div>
      <span className="activity-view__row-divider" aria-hidden="true" />
    </div>
  </div>
);

const ActivityRow = ({
  row,
  onOpen,
}: {
  row: ActivityRowData;
  onOpen: () => void;
}) => (
  <button
    type="button"
    className="activity-view__row activity-view__row--clickable"
    data-variant={row.variant}
    onClick={() => {
      row.onClick();
      onOpen();
    }}
  >
    <ActivityRowVisual variant={row.variant} image={row.image} />
    <div className="activity-view__row-body">
      <span
        className="activity-view__row-divider activity-view__row-divider--top"
        aria-hidden="true"
      />
      <div className="activity-view__row-main">
        <div className="activity-view__row-text">
          <p className="activity-view__row-title">{row.title}</p>
          {row.subtitle && (
            <p className="activity-view__row-subtitle">{row.subtitle}</p>
          )}
        </div>
        {typeof row.count === 'number' && row.count > 0 && (
          <span className="activity-view__row-badge">{row.count}</span>
        )}
      </div>
      <span className="activity-view__row-divider" aria-hidden="true" />
    </div>
  </button>
);

/**
 * Activity view (mobile-first), modeled on ToolsSheet as a global bottom-nav
 * destination: full-screen page below the shared header on mobile, centered
 * card on desktop. Renders three live sections (My places / My buying interests
 * / Following) from the ActivityResponse passed via props (JAA-284). Shows a
 * shimmer skeleton while loading. An account with nothing in any section sees the
 * same sections, each with its own empty-state promo (JAA-470). A section's count
 * is shown only when it is above zero. The "+" FAB offers only the add actions
 * not already shown as an empty section's promo CTA, and is hidden when both
 * are (JAA-478).
 * Built to the Figma "Activity" design (file 4FpcyaiutzOU5WsPhYu6DG, node 2039:11433).
 */
const ActivityView: React.FC<ActivityViewProps> = ({
  open,
  onClose,
  slideDir = 'forward',
  data,
  isLoading,
  onRowClick,
  onCouldSell,
  onCouldBuy,
  onNavigateExplore,
  isLoggedIn,
  setLoginModalVisible,
  initialRow,
  onInitialRowConsumed,
  covered = false,
}) => {
  const { t, i18n } = useTranslation();
  const [actionsOpen, setActionsOpen] = useState(false);
  const isDesktopCard = useMediaQuery(DESKTOP_CARD_QUERY);

  // The row whose matches are being shown; null is the section list. Held here
  // rather than routed, so the back chevron restores the list with its section
  // expansion intact.
  const [selectedRow, setSelectedRow] = useState<ActivityRowDetailRow | null>(
    null,
  );
  // Keeps the detail mounted for the length of its slide-out before the section
  // list comes back; without it the exit animation would never be seen.
  const [detailClosing, setDetailClosing] = useState(false);
  const closeDetail = useCallback(() => {
    setDetailClosing(true);
    window.setTimeout(() => {
      setSelectedRow(null);
      setDetailClosing(false);
    }, DETAIL_SLIDE_MS);
  }, []);

  // The section list and the row detail share this one scroller, so the detail's
  // own scrolling overwrites the list's offset and the list came back at the top
  // (JAA-345). Parked on every list scroll, put back when the list returns.
  const scrollRef = useRef<HTMLDivElement>(null);
  const listScrollTopRef = useRef(0);

  /** Records the section list's scroll offset, ignoring the detail's scrolling. */
  const handleScroll = () => {
    if (selectedRow) return;
    listScrollTopRef.current = scrollRef.current?.scrollTop ?? 0;
  };

  useLayoutEffect(() => {
    if (selectedRow || !scrollRef.current) return;
    scrollRef.current.scrollTop = listScrollTopRef.current;
  }, [selectedRow]);

  // Collapse the FAB actions whenever the view is dismissed so it never
  // re-opens mid-expanded on the next visit.
  useEffect(() => {
    if (!open) setActionsOpen(false);
  }, [open]);

  // Drop back to the section list when the view is dismissed, so reopening
  // Activity never lands mid-drilldown on a stale row.
  useEffect(() => {
    if (!open) setSelectedRow(null);
  }, [open]);

  // Accordion state: sections start collapsed to their SECTION_PREVIEW rows;
  // tapping a header expands to the full list.
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});
  const toggleSection = (id: string) =>
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));

  const sections = useMemo<ActivitySectionView[]>(() => {
    const { my_places, buying_interests, following } = data ?? EMPTY_ACTIVITY;

    const myPlacesRows: ActivityRowData[] = my_places.map((row) => ({
      id: row.building_id ?? row.willingness_id,
      variant: 'seller' as const,
      title: formatAddress(row.address),
      subtitle: activitySubtitle(
        [[row.buyer_count, 'newLandingPage.activity.buyers']],
        t,
      ),
      count: row.new_buyer_count,
      image: row.image,
      section: 'my_places' as const,
      entityId: row.building_id,
      onClick: () => onRowClick?.('my_places', row.building_id),
    }));

    const buyingRows: ActivityRowData[] = buying_interests.map((row) => {
      if (row.kind === 'area') {
        // Cast the opaque display_areas/criteria to AreaInterest so areasLabel
        // and criteriaStatsLabel can consume them — the backend shape is identical.
        const asInterest = {
          display_areas: row.display_areas,
          criteria: row.criteria,
        } as Pick<AreaInterest, 'display_areas' | 'criteria'>;
        return {
          id: row.area_interest_id,
          variant: 'buyer' as const,
          // Title leads with the facts a buyer scans by — size and rooms —
          // then the place, matching how a building-kind row reads as an
          // address. Price/floor/amenities stay out: they are the row's
          // criteria detail, not its identity, and overflowed the one line.
          title: [
            criteriaStatsLabel(asInterest as AreaInterest, t),
            areasLabel(
              asInterest as AreaInterest,
              t,
              anchorStreet(row.anchor_address),
            ),
          ]
            .filter(Boolean)
            .join(', '),
          subtitle: buyingMatchSubtitle(row, t),
          count: row.new_listing_count,
          section: 'buying_interests' as const,
          entityId: row.area_interest_id,
          onClick: () => onRowClick?.('buying_interests', row.area_interest_id),
        };
      }
      return {
        id: row.willingness_id,
        variant: 'buyer' as const,
        title: formatAddress(row.address),
        subtitle: buyingMatchSubtitle(row, t),
        count: row.new_listing_count,
        section: 'buying_interests' as const,
        entityId: row.willingness_id,
        onClick: () => onRowClick?.('buying_interests', row.willingness_id),
      };
    });

    const followingRows: ActivityRowData[] = following.map((row) => {
      const newTotal = row.new_count + row.new_like_count;
      return {
        id: row.building_id,
        variant: 'building' as RowVariant,
        // A handful of followed buildings have no address in any collection, so
        // the row would otherwise render a blank title.
        title:
          formatAddress(row.address) ||
          t('newLandingPage.activity.followingUnnamed', 'Followed building'),
        subtitle: activitySubtitle(
          [
            // Buyers lead so the "in this building" qualifier survives the subtitle's single-line ellipsis (JAA-348)
            [row.buyer_count, 'newLandingPage.activity.buyersInBuilding'],
            [row.like_count, 'newLandingPage.activity.likes'],
            [row.listing_count, 'newLandingPage.activity.listings'],
          ],
          t,
        ),
        count: newTotal,
        section: 'following' as const,
        entityId: row.building_id,
        onClick: () => onRowClick?.('following', row.building_id),
      };
    });

    return [
      {
        id: 'my-places',
        title: t('newLandingPage.activity.sections.myPlaces'),
        rows: myPlacesRows,
      },
      {
        id: 'buying-interests',
        title: t('newLandingPage.activity.sections.buyingInterests'),
        rows: buyingRows,
      },
      {
        id: 'following',
        title: t('newLandingPage.activity.sections.following'),
        rows: followingRows,
      },
    ];
  }, [data, t, onRowClick]);

  // Deep-linked row (digest email CTA): auto-open its detail as if tapped —
  // mark seen, then select. Skips while a fetch is in flight so a stale
  // payload (or the badge poll's empty stub) can't answer for fresh data, and
  // consumes the request only when the row is found; one that never resolves
  // (e.g. a deleted interest) is cleared by the parent when the view closes.
  useEffect(() => {
    if (!open || !initialRow || !data || isLoading) return;
    const row = sections
      .flatMap((section) => section.rows)
      .find(
        (r) =>
          r.section === initialRow.section && r.entityId === initialRow.entityId,
      );
    if (!row) return;
    onRowClick?.(row.section, row.entityId);
    setSelectedRow({
      section: row.section,
      entityId: row.entityId,
      variant: row.variant,
      title: row.title,
      subtitle: row.subtitle,
      count: row.count,
      image: row.image,
    });
    onInitialRowConsumed?.();
  }, [open, initialRow, data, isLoading, sections, onRowClick, onInitialRowConsumed]);


  const handleClose = () => {
    setActionsOpen(false);
    onClose();
  };

  const promoHandlers: Record<PromoAction, (() => void) | undefined> = {
    sell: onCouldSell,
    buy: onCouldBuy,
    explore: onNavigateExplore,
  };

  // Keep add controls hidden throughout loading; after loading, suppress only actions with a visible promo.
  const promoActions = !isLoading && !selectedRow
    ? sections.filter(isSectionEmpty).map((section) => SECTION_PROMOS[section.id].action)
    : [];
  const showSell = !promoActions.includes('sell');
  const showBuy = !promoActions.includes('buy');
  const hasFab = !isLoading && (showSell || showBuy);

  useLayoutEffect(() => {
    if (!hasFab) setActionsOpen(false);
  }, [hasFab]);

  if (!open) return null;

  const fabCluster = (
    <>
      {actionsOpen && (
        <div
          className="activity-view__fab-overlay"
          onClick={() => setActionsOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="activity-view__fab-wrap">
        {actionsOpen && (
          <div className="activity-view__fab-actions">
            {showSell && (
              <button
                type="button"
                className="activity-view__fab-action activity-view__fab-action--sell"
                onClick={() => {
                  setActionsOpen(false);
                  onCouldSell?.();
                }}
              >
                {t('newLandingPage.activity.fab.couldSell')}
              </button>
            )}
            {showBuy && (
              <button
                type="button"
                className="activity-view__fab-action activity-view__fab-action--buy"
                onClick={() => {
                  setActionsOpen(false);
                  onCouldBuy?.();
                }}
              >
                {t('newLandingPage.activity.fab.interestInBuying')}
              </button>
            )}
          </div>
        )}
        <button
          type="button"
          className={`activity-view__fab-main${actionsOpen ? ' activity-view__fab-main--active' : ''}`}
          aria-label={
            actionsOpen
              ? t('newLandingPage.activity.fab.closeAria')
              : t('newLandingPage.activity.fab.openAria')
          }
          aria-expanded={actionsOpen}
          onClick={() => setActionsOpen((prev) => !prev)}
        >
          {actionsOpen ? <CloseGlyph /> : <PlusIcon />}
        </button>
      </div>
    </>
  );

  return (
    <div
      className={`activity-view${covered ? ' activity-view--covered' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-hidden={covered}
      aria-label={t('newLandingPage.activity.title')}
    >
      <div className="activity-view__backdrop" onClick={handleClose} />
      <div className="activity-view__panel" data-slide={slideDir}>
        <div
          className={`activity-view__scroll${hasFab ? '' : ' activity-view__scroll--no-fab'}`}
          ref={scrollRef}
          onScroll={handleScroll}
        >
          {selectedRow && (
            <ActivityRowDetail
              row={selectedRow}
              onBack={closeDetail}
              closing={detailClosing}
              isLoggedIn={isLoggedIn}
              setLoginModalVisible={setLoginModalVisible}
            />
          )}
          {!selectedRow && isLoading && (
            <div className="activity-view__sections">
              {[
                {
                  id: 'my-places',
                  key: 'newLandingPage.activity.sections.myPlaces',
                },
                {
                  id: 'buying-interests',
                  key: 'newLandingPage.activity.sections.buyingInterests',
                },
                {
                  id: 'following',
                  key: 'newLandingPage.activity.sections.following',
                },
              ].map((s) => (
                <section key={s.id} className="activity-view__section">
                  <p className="activity-view__section-title">{t(s.key)}</p>
                  {/* One placeholder per row the section actually previews, so
                      the skeleton doesn't reflow when the real rows land. */}
                  {Array.from({ length: SECTION_PREVIEW[s.id] ?? 1 }, (_, i) => (
                    <ShimmerRow key={i} />
                  ))}
                </section>
              ))}
            </div>
          )}
          {!selectedRow && !isLoading && (
            <div className="activity-view__sections">
              {sections.map((section) => {
                const preview = SECTION_PREVIEW[section.id] ?? 1;
                const isEmpty = isSectionEmpty(section);
                const hasMore = section.rows.length > preview;
                const previewRows = section.rows.slice(0, preview);
                const extraRows = section.rows.slice(preview);
                const promo = isEmpty ? SECTION_PROMOS[section.id] : undefined;
                const onPromoCta = promo
                  ? promoHandlers[promo.action]
                  : undefined;
                // The chevron appears only when there is something to expand, so
                // a section showing all its rows has none, and an empty section
                // can't be collapsed to hide its own promo.
                const open = isEmpty || !!expandedSections[section.id];
                const hasChevron = hasMore;
                const onHeaderToggle = hasMore
                  ? () => toggleSection(section.id)
                  : undefined;
                return (
                  <section key={section.id} className="activity-view__section">
                    <button
                      type="button"
                      className="activity-view__section-header"
                      onClick={onHeaderToggle}
                      aria-expanded={hasChevron ? open : undefined}
                      disabled={!hasChevron}
                    >
                      <span className="activity-view__section-title">
                        {section.title}
                        {section.rows.length > 0 && (
                          <span className="activity-view__section-count">
                            {section.rows.length}
                          </span>
                        )}
                      </span>
                      {hasChevron && (
                        <span
                          className={`activity-view__section-chevron${
                            open ? ' activity-view__section-chevron--open' : ''
                          }`}
                          aria-hidden="true"
                        >
                          <ChevronIcon />
                        </span>
                      )}
                    </button>
                    {promo ? (
                      <div
                        className={`activity-view__section-extra${
                          open ? ' activity-view__section-extra--open' : ''
                        }`}
                      >
                        <div className="activity-view__section-extra-inner activity-view__section-extra-inner--promo">
                          <div className="activity-view__promo">
                            <div className="activity-view__promo-body">
                              <span className="activity-view__promo-icon">
                                {promo.iconVariant === 'seller' && (
                                  <img
                                    src={houseIcon}
                                    alt=""
                                    aria-hidden="true"
                                  />
                                )}
                                {promo.iconVariant === 'buyer' && (
                                  <img
                                    src={coinIcon}
                                    alt=""
                                    aria-hidden="true"
                                  />
                                )}
                                {promo.iconVariant === 'building' && (
                                  <BuildingGlyph />
                                )}
                              </span>
                              <p className="activity-view__promo-text">
                                {t(promo.textKey)}{' '}
                                <a
                                  className="activity-view__promo-learn"
                                  href={
                                    i18n.language.startsWith('fi')
                                      ? promo.learnUrl.fi
                                      : promo.learnUrl.en
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {t('newLandingPage.activity.promo.learnMore')}
                                </a>
                              </p>
                            </div>
                            <button
                              type="button"
                              className={`activity-view__promo-cta activity-view__promo-cta--${promo.tone}`}
                              onClick={onPromoCta}
                            >
                              {t(promo.ctaKey)}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        {previewRows.map((row) => (
                          <ActivityRow
                            key={row.id}
                            row={row}
                            onOpen={() =>
                              setSelectedRow({
                                section: row.section,
                                entityId: row.entityId,
                                variant: row.variant,
                                title: row.title,
                                subtitle: row.subtitle,
                                count: row.count,
                                image: row.image,
                              })
                            }
                          />
                        ))}
                        {hasMore && (
                          <div
                            className={`activity-view__section-extra${
                              open ? ' activity-view__section-extra--open' : ''
                            }`}
                          >
                            <div className="activity-view__section-extra-inner">
                              {extraRows.map((row) => (
                                <ActivityRow
                                  key={row.id}
                                  row={row}
                                  onOpen={() =>
                                    setSelectedRow({
                                      section: row.section,
                                      entityId: row.entityId,
                                      variant: row.variant,
                                      title: row.title,
                                      subtitle: row.subtitle,
                                      count: row.count,
                                      image: row.image,
                                    })
                                  }
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </div>
        {isDesktopCard && hasFab && fabCluster}
      </div>
      {!isDesktopCard && hasFab && fabCluster}
    </div>
  );
};

export default ActivityView;
